/**
 * Helpers for displaying server (UTC) timestamps in an office time zone.
 */

const ISO_OFFSET_RE = /[+-]\d{2}:\d{2}$|Z$/i;

export const DEFAULT_TIME_ZONE = 'America/New_York'; // Eastern with DST

const US_TIME_ZONE_TO_IANA = {
  EST: 'America/New_York',
  EDT: 'America/New_York',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles'
};

// Old dropdown/backend stored standard US labels as fixed daylight offsets
// (e.g. MST -> GMT-06:00). Convert those exact strings to the correct IANA
// zones so the UI shows the right region and follows DST.
const LEGACY_OFFSET_TO_IANA = {
  'GMT-04:00': 'America/New_York',
  'GMT-05:00': 'America/Chicago',
  'GMT-06:00': 'America/Denver',
  'GMT-07:00': 'America/Los_Angeles'
};

const TIME_ZONE_TO_OFFSET = {
  EST: 'GMT-05:00',
  CST: 'GMT-06:00',
  MST: 'GMT-07:00',
  PST: 'GMT-08:00',
  EDT: 'GMT-04:00',
  CDT: 'GMT-05:00',
  MDT: 'GMT-06:00',
  PDT: 'GMT-07:00'
};

/**
 * Resolve a user-friendly or IANA time zone into the form used for display.
 * US abbreviations are mapped to IANA locations that observe DST; old fixed
 * offsets from the legacy dropdown are mapped to their region; known offset
 * strings and IANA values are preserved. Falls back to America/New_York.
 */
export const resolveTimeZone = (timeZone) => {
  if (!timeZone) return DEFAULT_TIME_ZONE;
  const upper = String(timeZone).trim().toUpperCase();
  return US_TIME_ZONE_TO_IANA[upper] || LEGACY_OFFSET_TO_IANA[upper] || TIME_ZONE_TO_OFFSET[upper] || timeZone;
};

const getShortTimeZoneName = (iana, date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'short'
    }).formatToParts(date);
    const part = parts.find((p) => p.type === 'timeZoneName');
    return part ? part.value : iana;
  } catch {
    return iana;
  }
};

/**
 * Return a friendly label for a time zone value. IANA and US abbreviations
 * return the current short name (EST/EDT, PST/PDT, etc.). Offsets are mapped
 * to their standard labels. Unknown values are returned as-is.
 */
export const formatTimeZoneLabel = (timeZone, date = new Date()) => {
  if (!timeZone) return getShortTimeZoneName(DEFAULT_TIME_ZONE, date);

  const upper = String(timeZone).trim().toUpperCase();
  if (US_TIME_ZONE_TO_IANA[upper]) {
    return getShortTimeZoneName(US_TIME_ZONE_TO_IANA[upper], date);
  }

  const resolved = resolveTimeZone(timeZone);
  if (resolved && !/^GMT[+-]/.test(resolved)) {
    return getShortTimeZoneName(resolved, date);
  }

  const match = Object.keys(TIME_ZONE_TO_OFFSET).find((key) => TIME_ZONE_TO_OFFSET[key] === resolved);
  return match || resolved;
};

const TIME_ZONE_COLORS = {
  EST: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', icon: 'text-blue-500' },
  CST: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'text-emerald-500' },
  MST: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: 'text-amber-500' },
  PST: { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200', icon: 'text-violet-500' }
};

const DAYLIGHT_VARIANTS = {
  EDT: 'EST',
  CDT: 'CST',
  MDT: 'MST',
  PDT: 'PST'
};

export const getTimeZoneColor = (timeZone, date = new Date()) => {
  const label = formatTimeZoneLabel(timeZone, date);
  const base = DAYLIGHT_VARIANTS[label] || label;
  return TIME_ZONE_COLORS[base] || TIME_ZONE_COLORS.EST;
};

/**
 * Parse a server timestamp as a Date in UTC. Server LocalDateTime values have
 * no offset, so we append Z. Strings that already include an offset are parsed
 * as-is.
 */
export const parseServerTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const normalized = String(timestamp).trim();
  if (ISO_OFFSET_RE.test(normalized)) {
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(`${normalized}Z`);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const toIanaTimeZone = (timeZone) => {
  if (!timeZone) return DEFAULT_TIME_ZONE;
  const resolved = resolveTimeZone(timeZone);
  const match = resolved.match(/^GMT([+-])(\d{2}):(\d{2})$/);
  if (!match) return resolved;
  const [, sign, hours, minutes] = match;
  const ianaSign = sign === '-' ? '+' : '-';
  if (minutes === '00') {
    return `Etc/GMT${ianaSign}${Number(hours)}`;
  }
  return `Etc/GMT${ianaSign}${Number(hours)}:${minutes}`;
};

const withTimeZone = (options, timeZone) =>
  ({ ...options, timeZone: toIanaTimeZone(timeZone) });

export const formatZonedTime = (timestamp, timeZone = null) => {
  const date = parseServerTimestamp(timestamp);
  if (!date) return '';
  return date.toLocaleTimeString('en-US', withTimeZone({ hour: '2-digit', minute: '2-digit' }, timeZone));
};

const sameZonedDay = (dateA, dateB, timeZone = null) => {
  const format = (date) =>
    new Intl.DateTimeFormat('en-US', withTimeZone({ year: 'numeric', month: '2-digit', day: '2-digit' }, timeZone)).format(date);
  return format(dateA) === format(dateB);
};

export const formatZonedDate = (timestamp, timeZone = null) => {
  const date = parseServerTimestamp(timestamp);
  if (!date) return '';
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  if (sameZonedDay(date, now, timeZone)) return 'Today';
  if (sameZonedDay(date, yesterday, timeZone)) return 'Yesterday';
  return date.toLocaleDateString('en-US', withTimeZone({ month: 'short', day: 'numeric' }, timeZone));
};

export const groupKeyForZonedDate = (timestamp, timeZone = null) => {
  const date = parseServerTimestamp(timestamp);
  if (!date) return 'Unknown';
  return new Intl.DateTimeFormat(
    'en-US',
    withTimeZone({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }, timeZone)
  ).format(date);
};

export const formatZonedDateTime = (timestamp, timeZone = null) => {
  const date = parseServerTimestamp(timestamp);
  if (!date) return '-';
  return date.toLocaleString('en-US', withTimeZone({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }, timeZone));
};

/**
 * Format the time remaining until a server timestamp.
 */
export const formatRemainingTime = (timestamp, now = new Date(), { includeSeconds = false } = {}) => {
  const date = parseServerTimestamp(timestamp);
  if (!date) return '-';

  const diff = date.getTime() - now.getTime();
  if (diff < -60000) return 'overdue';
  if (diff <= 0) return 'due now';
  if (diff < 60000 && !includeSeconds) return '< 1 min';

  const totalSeconds = Math.max(0, Math.ceil(diff / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  if (includeSeconds) {
    // Show seconds for near-term countdowns so the UI ticks down live.
    const shouldShowSeconds = days === 0 && hours === 0 && minutes < 10 && seconds > 0;
    if (shouldShowSeconds) parts.push(`${seconds}s`);
  }

  if (parts.length === 0) return seconds > 0 ? `${seconds}s` : 'due now';
  return parts.join(' ');
};

const DATE_TIME_PARTS_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

/**
 * Format an appointment date/time without applying a time zone. Appointments
 * are stored as the desired wall-clock time, so we just display the components
 * exactly as provided.
 */
export const formatAppointmentDateTime = (timestamp) => {
  if (!timestamp) return '-';
  const match = String(timestamp).trim().match(DATE_TIME_PARTS_RE);
  if (!match) return '-';

  const [, year, month, day, hour, minute, second = '00'] = match;
  const date = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second))
  );

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC'
  });
};
